import { Button } from "@/components/ui/button";
import intelLogo from "@/assets/intel-logo.png";
import nestVcLogo from "@/assets/nest-vc-logo.png";
import hsbcLogo from "@/assets/hsbc-logo.png";
import dbsLogo from "@/assets/dbs-logo.png";
import aiaLogo from "@/assets/aia-logo.jpg";
import infinitiLogo from "@/assets/infiniti-logo.png";
import ocbcLogo from "@/assets/ocbc-logo.png";
import speakingPanel from "@/assets/speaking-panel.jpg";
import aiaAcceleratorGroup from "@/assets/aia-accelerator-group.jpg";
import taiwanMentorship from "@/assets/taiwan-mentorship.jpg";
import startupTeamMeeting from "@/assets/startup-team-meeting.jpg";
import aiaDemoDay from "@/assets/aia-demo-day.jpg";
import foundersGroup from "@/assets/founders-group.jpg";

import { useLanguage } from "@/i18n/LanguageContext";
import type { TranslationKey } from "@/i18n/translations";

const AboutSection = () => {
  const { t } = useLanguage();

  const companies = [
    { name: "Intel", logo: intelLogo },
    { name: "HSBC Asset Management", logo: hsbcLogo },
    { name: "DBS Bank", logo: dbsLogo },
    { name: "AIA Insurance", logo: aiaLogo },
    { name: "Infiniti", logo: infinitiLogo },
    { name: "OCBC Bank", logo: ocbcLogo },
  ];

  const workValues: TranslationKey[] = [
    "about.work.item1",
    "about.work.item2",
    "about.work.item3",
    "about.work.item4",
  ];

  const founderValues: TranslationKey[] = [
    "about.values.item1",
    "about.values.item2",
    "about.values.item3",
    "about.values.item4",
  ];

  return (
    <section id="about" className="py-32 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* How this shows up in my work */}
          <div className="mt-24 grid md:grid-cols-2 gap-12">
            <div className="bg-secondary/30 rounded-sm p-8 border border-border/30">
              <h3 className="text-xl font-bold mb-6">{t("about.work.title")}</h3>
              <p className="text-muted-foreground mb-6 font-serif">{t("about.work.intro")}</p>
              <ul className="space-y-3">
                {workValues.map((value, index) => (
                  <li key={index} className="flex items-start gap-3 text-foreground/80 font-serif">
                    <span className="text-accent font-bold">•</span>
                    {t(value)}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-foreground font-medium font-serif">
                {t("about.work.close")}
              </p>
            </div>

            <div className="bg-secondary/30 rounded-sm p-8 border border-border/30">
              <h3 className="text-xl font-bold mb-6">{t("about.values.title")}</h3>
              <ul className="space-y-3 mb-6">
                {founderValues.map((value, index) => (
                  <li key={index} className="flex items-start gap-3 text-foreground/80 font-serif">
                    <span className="text-accent font-bold">•</span>
                    {t(value)}
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground font-serif italic">
                {t("about.values.close")}
              </p>
            </div>
          </div>

          <div className="mt-32">
            <div className="text-center mb-16 space-y-4">
              <h3 className="text-3xl sm:text-4xl font-bold tracking-tighter">
                {t("about.gallery.title")}
              </h3>
              <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
                {t("about.gallery.subtitle")}
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="overflow-hidden rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover-lift">
                <img
                  src={speakingPanel}
                  alt="Peter Dingle speaking at Finnotec conference panel"
                  className="w-full h-72 object-cover"
                  loading="lazy"
                />
              </div>
              
              <div className="overflow-hidden rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover-lift">
                <img
                  src={aiaAcceleratorGroup}
                  alt="AIA Accelerator Orientation Day with startup founders"
                  className="w-full h-72 object-cover"
                  loading="lazy"
                />
              </div>
              
              <div className="overflow-hidden rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover-lift">
                <img
                  src={taiwanMentorship}
                  alt="Mentoring students and founders in Taiwan"
                  className="w-full h-72 object-cover"
                  loading="lazy"
                />
              </div>
              
              <div className="overflow-hidden rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover-lift">
                <img
                  src={startupTeamMeeting}
                  alt="Strategy session with startup team"
                  className="w-full h-72 object-cover"
                  loading="lazy"
                />
              </div>
              
              <div className="overflow-hidden rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover-lift">
                <img
                  src={aiaDemoDay}
                  alt="AIA Accelerator Demo Day 2015"
                  className="w-full h-72 object-cover"
                  loading="lazy"
                />
              </div>
              
              <div className="overflow-hidden rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover-lift">
                <img
                  src={foundersGroup}
                  alt="Founders cohort group photo"
                  className="w-full h-72 object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.open("https://calendar.app.google/smFTnDe7izrtAPVg6", "_blank")}
              className="font-sans"
            >
              {t("about.cta")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
